import {
  X,
  CheckCircle2,
  Clock,
  CircleDashed,
  Calendar,
  Receipt,
  Wallet,
  ListChecks,
} from "lucide-react";

export interface MilestoneItem {
  order: number;
  title: string;
  amount: number;
  status: string;
  dueDate: string | null;
  releasedAt: string | null;
  notes: string;
}

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusInfo = (status: string) => {
  if (status === "Released")
    return {
      label: "Released",
      cls: "bg-emerald-100 text-emerald-700 ring-emerald-200",
      icon: <CheckCircle2 size={12} />,
    };
  if (status === "In Escrow")
    return {
      label: "In Escrow",
      cls: "bg-amber-100 text-amber-700 ring-amber-200",
      icon: <Clock size={12} />,
    };
  return {
    label: "Pending",
    cls: "bg-slate-100 text-slate-600 ring-slate-200",
    icon: <CircleDashed size={12} />,
  };
};

interface Props {
  projectName: string;
  projectType?: string;
  milestones: MilestoneItem[];
  onClose: () => void;
}

const MilestoneModal = ({
  projectName,
  projectType,
  milestones,
  onClose,
}: Props) => {
  const totalValue = milestones.reduce((s, m) => s + (m.amount || 0), 0);
  const released = milestones
    .filter((m) => m.status === "Released")
    .reduce((s, m) => s + (m.amount || 0), 0);
  const inEscrow = milestones
    .filter((m) => m.status === "In Escrow")
    .reduce((s, m) => s + (m.amount || 0), 0);
  const pending = totalValue - released;
  const releasedPct = totalValue
    ? Math.round((released / totalValue) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >

        <div className="p-6 border-b border-slate-200 bg-gradient-to-br from-blue-50/60 to-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks size={18} className="text-[#0058c3]" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                Milestone Breakdown
              </p>
            </div>
            <h2 className="text-[20px] font-extrabold text-slate-900 leading-tight truncate">
              {projectName}
            </h2>
            {projectType && (
              <p className="text-[12px] text-slate-500 mt-0.5">{projectType}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Overall Progress
            </span>
            <span className="text-[12px] font-extrabold text-slate-900">
              {milestones.filter((m) => m.status === "Released").length}/
              {milestones.length} released · {releasedPct}%
            </span>
          </div>
          <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${
                releasedPct === 100
                  ? "bg-emerald-500"
                  : "bg-gradient-to-r from-blue-400 to-blue-600"
              }`}
              style={{ width: `${releasedPct}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-5 space-y-2">
          {milestones.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-[13px]">
              No milestones configured for this project yet.
            </div>
          ) : (
            milestones.map((m) => {
              const s = statusInfo(m.status);
              return (
                <div
                  key={m.order}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50/50 transition"
                >
                  <div className="w-8 h-8 grid place-items-center rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-600 shrink-0">
                    {m.order}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-900 truncate">
                      {m.title || `Milestone ${m.order}`}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                      {m.dueDate && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={10} />
                          Due {formatDate(m.dueDate)}
                        </span>
                      )}
                      {m.releasedAt && (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 size={10} />
                          Released {formatDate(m.releasedAt)}
                        </span>
                      )}
                      {m.notes && (
                        <span className="truncate max-w-[180px]">
                          “{m.notes}”
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[14px] font-extrabold text-slate-900 font-mono">
                      {fmtMoney(m.amount)}
                    </p>
                    <span
                      className={`inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${s.cls}`}
                    >
                      {s.icon}
                      {s.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-5 border-t border-slate-200 bg-slate-50/60">
          <div className="grid grid-cols-3 gap-3">
            <SummaryCard
              icon={Wallet}
              label="Total Project Value"
              value={fmtMoney(totalValue)}
              tone="blue"
            />
            <SummaryCard
              icon={CheckCircle2}
              label="Released"
              value={fmtMoney(released)}
              tone="emerald"
            />
            <SummaryCard
              icon={Receipt}
              label="Pending"
              value={fmtMoney(pending)}
              sub={inEscrow > 0 ? `${fmtMoney(inEscrow)} in escrow` : undefined}
              tone="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: any;
  label: string;
  value: string;
  sub?: string | undefined;
  tone: "blue" | "emerald" | "amber";
}) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className={`p-3 rounded-lg ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={12} />
        <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">
          {label}
        </span>
      </div>
      <p className="text-[18px] font-extrabold font-mono leading-none">
        {value}
      </p>
      {sub && <p className="text-[10px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
};

export default MilestoneModal;
